import React from "react";
import { motion } from "framer-motion";
import { useBrand } from "@/context/BrandContext";
import { IMAGES } from "@/lib/brands";
import { ArrowRight, MapPin, Phone } from "lucide-react";

const Hero = () => {
  const { brand, sister } = useBrand();

  return (
    <section
      className="relative min-h-[90vh] bg-[color:var(--bg-dark)] text-white overflow-hidden isolate"
      data-testid="hero-section"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMAGES.hero})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/85" />
      <div className="absolute inset-0 grid-lines-dark opacity-40" />
      <div className="absolute inset-0 noise" />

      <div className="relative container-x pt-28 md:pt-36 pb-24 md:pb-32 flex flex-col min-h-[90vh] justify-between">
        <div className="flex items-center gap-4">
          <span className="eyebrow text-[color:var(--accent)]">
            ◆ {brand.city}, {brand.state}
          </span>
          <span className="h-px w-24 bg-white/30" />
          <span className="eyebrow text-white/60">{brand.established} In Business</span>
        </div>

        <div className="max-w-5xl">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-display uppercase text-[52px] sm:text-[84px] md:text-[120px] leading-[0.88] tracking-tighter"
            data-testid="hero-title"
          >
            It's More <br />
            Than Just <br />
            <span className="text-[color:var(--accent)]">Scrap.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="mt-8 max-w-xl text-white/80 text-base md:text-lg leading-relaxed"
          >
            {brand.tagline}. A full metal recycling facility — minority
            women-owned, committed to responsible processing, competitive
            pricing, and the communities we serve.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <a
              href="#materials"
              className="hard-btn bg-[color:var(--accent)] text-white border-[color:var(--accent)] hover:bg-transparent"
              data-testid="hero-cta-materials"
            >
              What We Buy <ArrowRight size={14} />
            </a>
            <a
              href="#contact"
              className="hard-btn-inverse"
              data-testid="hero-cta-contact"
            >
              Get In Touch
            </a>
          </motion.div>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-white/15 pt-10">
          <div className="flex items-start gap-3">
            <MapPin size={18} className="shrink-0 text-[color:var(--accent)]" />
            <div>
              <div className="eyebrow text-white/50 mb-1">This Yard</div>
              <div className="text-sm text-white">{brand.address}</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Phone size={18} className="shrink-0 text-[color:var(--accent)]" />
            <div>
              <div className="eyebrow text-white/50 mb-1">Call</div>
              <a href={`tel:${brand.phone.replace(/[^0-9]/g, "")}`} className="text-sm text-white">
                {brand.phone}
              </a>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-[18px] h-[18px] shrink-0 grid place-items-center text-[color:var(--accent)]">
              ◆
            </div>
            <div>
              <div className="eyebrow text-white/50 mb-1">Sister Yard</div>
              <div className="text-sm text-white">
                {sister.short_name} · {sister.city}, {sister.state}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
